import * as Sentry from '@sentry/react'
import { useEffect } from 'react'

import { StatusSession } from '@/lib/auth/AuthClient'
import { useSession } from '@/lib/auth/hooks'

/**
 * Renseigne l'utilisateur courant dans le scope Sentry.
 *
 * Sans ce composant, toutes les erreurs JS remontaient anonymes : impossible de
 * savoir qui subissait un bug, ni de recouper avec les erreurs de l'API (qui,
 * elle, pose deja son user via SentryUserContextListener).
 *
 * Monte sous AuthProvider mais AU-DESSUS des shells backoffice et patient, pour
 * couvrir les deux applications sans dependre du chargement d'un profil.
 * Ne rend rien.
 */
const SentryUserSync = () => {
  const session = useSession()

  const userId = session?.user?.id
  const userEmail = session?.user?.email
  const { switchUser, status } = session ?? {}

  useEffect(() => {
    if (status !== StatusSession.AUTHENTICATED || !userId) {
      // Deconnexion (ou session expiree) : on purge, sinon les erreurs
      // suivantes resteraient attribuees a l'utilisateur precedent.
      Sentry.setUser(null)
      Sentry.setTag('user.is_impersonated', undefined)

      return
    }

    // `user.id` reste celui de l'ADMIN connecte pendant un switch : la cible est
    // portee par `switchUser`. On expose les deux, avec la meme semantique que
    // l'API — `user` = identite effective des requetes, `impersonator` = qui agit.
    Sentry.setUser(
      switchUser
        ? { id: String(switchUser), impersonator_id: userId, impersonator_email: userEmail }
        : { id: String(userId), email: userEmail }
    )
    Sentry.setTag('user.is_impersonated', switchUser ? 'true' : undefined)
  }, [status, userId, userEmail, switchUser])

  return null
}

export default SentryUserSync
